import React, { useEffect, useState } from "react";
import { Plus, Edit2, Trash2, Save, X, Tag } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, notifyError } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";

const slugify = (s) =>
  (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export default function CategoriesPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | category id
  const [form, setForm] = useState({ name: "", slug: "", icon: "", order: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/categories");
      setItems(data || []);
    } catch (e) { notifyError(e); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    setEditing("new");
    setForm({ name: "", slug: "", icon: "", order: items.length });
  };
  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ name: c.name, slug: c.slug, icon: c.icon || "", order: c.order ?? 0 });
  };
  const cancel = () => { setEditing(null); setForm({ name: "", slug: "", icon: "", order: 0 }); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Nama kategori wajib diisi"); return; }
    if (!form.slug.trim()) { setForm((f) => ({ ...f, slug: slugify(f.name) })); }
    const payload = { ...form, slug: form.slug.trim() || slugify(form.name), order: Number(form.order) || 0 };
    setBusy(true);
    try {
      if (editing === "new") {
        await api.post("/categories", payload);
        toast.success("Kategori dibuat");
      } else {
        await api.put(`/categories/${editing}`, payload);
        toast.success("Kategori diperbarui");
      }
      cancel();
      load();
    } catch (e) {
      // Surface validation errors (duplicate, empty, etc) sebagai toast
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Hapus kategori "${c.name}"?`)) return;
    try {
      await api.delete(`/categories/${c.id}`);
      toast.success("Kategori dihapus");
      load();
    } catch (e) {
      // Backend kirim 400 + pesan "sedang digunakan oleh X produk" — tampilkan apa adanya
      toast.error(formatApiError(e));
    }
  };

  return (
    <AdminLayout title="Kategori Produk">
      <div className="max-w-3xl space-y-5" data-testid="admin-categories-page">
        <div className="flex items-center justify-between">
          <div className="text-sm text-[#5C5C5C]">
            Kelola kategori produk. Kategori yang sedang dipakai produk aktif tidak bisa dihapus.
          </div>
          <Button onClick={startNew} disabled={editing === "new"} className="bg-[#4A2E1F] text-white rounded-xl" data-testid="add-category-btn">
            <Plus size={16} className="mr-1" /> Tambah Kategori
          </Button>
        </div>

        {editing && (
          <Card className="p-5 space-y-3" data-testid="category-form">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <Tag size={16} /> {editing === "new" ? "Kategori Baru" : "Edit Kategori"}
            </h3>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label>Nama *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value, slug: form.slug || slugify(e.target.value) })}
                  placeholder="mis. Telur Ayam Premium"
                  data-testid="category-name-input"
                />
              </div>
              <div>
                <Label>Slug *</Label>
                <Input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                  placeholder="auto dari nama"
                  data-testid="category-slug-input"
                />
              </div>
              <div>
                <Label>Icon (emoji/url, opsional)</Label>
                <Input value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} placeholder="🥚 atau /uploads/..." data-testid="category-icon-input" />
              </div>
              <div>
                <Label>Urutan</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid="category-order-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={busy} className="bg-[#4A2E1F] text-white rounded-xl" data-testid="category-save-btn">
                <Save size={16} className="mr-1" /> {busy ? "Menyimpan..." : "Simpan"}
              </Button>
              <Button onClick={cancel} variant="outline" className="rounded-xl" data-testid="category-cancel-btn">
                <X size={16} className="mr-1" /> Batal
              </Button>
            </div>
          </Card>
        )}

        <Card className="p-0 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF9F6] border-b border-[#E5E5E5]">
              <tr className="text-left text-[10px] uppercase tracking-widest text-[#5C5C5C] font-bold">
                <th className="py-3 px-4">Icon</th>
                <th className="py-3 px-4">Nama</th>
                <th className="py-3 px-4">Slug</th>
                <th className="py-3 px-4 text-center">Urutan</th>
                <th className="py-3 px-4 w-32">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-b border-[#E5E5E5] last:border-0 hover:bg-[#FAF9F6]" data-testid={`category-row-${c.slug}`}>
                  <td className="py-3 px-4 text-xl">{c.icon || "🏷️"}</td>
                  <td className="py-3 px-4 font-medium">{c.name}</td>
                  <td className="py-3 px-4 font-mono text-xs text-[#5C5C5C]">{c.slug}</td>
                  <td className="py-3 px-4 text-center">{c.order ?? 0}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" onClick={() => startEdit(c)} data-testid={`edit-category-${c.slug}`}>
                        <Edit2 size={14} />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(c)} data-testid={`delete-category-${c.slug}`}>
                        <Trash2 size={14} className="text-red-600" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-xs text-[#5C5C5C]">Belum ada kategori. Klik "Tambah Kategori" untuk mulai.</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </AdminLayout>
  );
}
